import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { loanAPI, LoanApplicationRequest } from '../../services/api';
import StepIndicator from './StepIndicator';
import Step1Amount from './steps/Step1Amount';
import Step2PersonalDetails from './steps/Step2PersonalDetails';
import Step3BankAccount from './steps/Step3BankAccount';
import Step4NSFASVerification from './steps/Step4NSFASVerification';
import Step5TermsAndConditions from './steps/Step5TermsAndConditions';
import './LoanApplicationFlow.css';

export interface ApplicationData {
  // Step 1: Amount
  loanAmount: number;
  
  // Step 2: Personal Details
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  saIdNumber: string;
  
  // Step 3: Bank Account
  bankName: string;
  accountNumber: string;
  accountType: 'savings' | 'cheque';
  branchCode: string;
  
  // Step 4: NSFAS Verification
  nsfasStatement?: File;
  bankStatement?: File;
  studentCard?: File;
  institutionName: string;
  studentNumber: string;
  studyYear: string;
  nsfasMonthlyAllowance: string;
  hasOtherCredit: 'yes' | 'no' | '';
  otherCreditMonthly: string;
  
  // Step 5: Terms
  termsAccepted: boolean;
  privacyAccepted: boolean;
  creditCheckConsent: boolean;
}

const STEPS = [
  { id: 1, title: 'Loan Amount', description: 'Confirm your loan amount' },
  { id: 2, title: 'Personal Details', description: 'Verify your information' },
  { id: 3, title: 'Bank Account', description: 'Add your bank details' },
  { id: 4, title: 'NSFAS Verification', description: 'Upload required documents' },
  { id: 5, title: 'Terms & Conditions', description: 'Review and accept terms' }
];

const LoanApplicationFlow: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [eligibilityBlock, setEligibilityBlock] = useState<string | null>(null);
  
  // Initialize application data with user's existing information
  const [applicationData, setApplicationData] = useState<ApplicationData>({
    loanAmount: parseInt(searchParams.get('amount') || '400') || 400,
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phoneNumber: user?.phone_number || '',
    saIdNumber: user?.sa_id_number || '',
    bankName: user?.bank_name || '',
    accountNumber: user?.bank_account_number || '',
    accountType: 'savings',
    branchCode: user?.bank_branch_code || '',
    institutionName: '',
    studentNumber: '',
    studyYear: '',
    nsfasMonthlyAllowance: '',
    hasOtherCredit: '',
    otherCreditMonthly: '',
    termsAccepted: false,
    privacyAccepted: false,
    creditCheckConsent: false
  });

  // Redirect to auth if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth?redirect=/apply');
    }
  }, [isAuthenticated, navigate]);

  // Check loan eligibility on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    loanAPI.checkEligibility().then(res => {
      if (res.success && !res.data.eligible) {
        setEligibilityBlock(res.data.reasons[0] || 'You are not eligible for a new loan right now.');
      }
    }).catch(() => { /* fail open — backend submit will still enforce */ });
  }, [isAuthenticated]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Update application data
  const updateApplicationData = (stepData: Partial<ApplicationData>) => {
    setApplicationData(prev => ({ ...prev, ...stepData }));
  };

  // Navigate to next step
  const goToNextStep = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
      // Scroll to top when moving to next step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Navigate to previous step
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Scroll to top when moving to previous step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Submit application
  const submitApplication = async () => {
    setIsSubmitting(true);

    try {
      // Prepare application request
      const applicationRequest: LoanApplicationRequest = {
        loanAmount: applicationData.loanAmount,
        firstName: applicationData.firstName,
        lastName: applicationData.lastName,
        email: applicationData.email,
        phoneNumber: applicationData.phoneNumber,
        saIdNumber: applicationData.saIdNumber,
        bankName: applicationData.bankName,
        accountNumber: applicationData.accountNumber,
        accountType: applicationData.accountType,
        branchCode: applicationData.branchCode,
        institutionName: applicationData.institutionName,
        studentNumber: applicationData.studentNumber,
        studyYear: applicationData.studyYear,
        nsfasMonthlyAllowance: applicationData.nsfasMonthlyAllowance,
        hasOtherCredit: applicationData.hasOtherCredit,
        otherCreditMonthly: applicationData.otherCreditMonthly,
        nsfasStatement: applicationData.nsfasStatement,
        bankStatement: applicationData.bankStatement,
        studentCard: applicationData.studentCard,
        termsAccepted: applicationData.termsAccepted,
        privacyAccepted: applicationData.privacyAccepted,
        creditCheckConsent: applicationData.creditCheckConsent
      };

      const response = await loanAPI.submitApplication(applicationRequest);
      
      if (response.success) {
        navigate('/application/success', {
          state: {
            loanNumber: response.data?.loanNumber,
            submittedAt: response.data?.createdAt || new Date().toISOString(),
          },
        });
      } else {
        throw new Error(response.message || 'Failed to submit application');
      }

    } catch (error: any) {
      console.error('Application submission failed:', error);

      // If the backend returned eligibility reasons, show the first one
      const reasons = error.response?.data?.reasons;
      const errorMessage = reasons?.length
        ? reasons[0]
        : error.response?.data?.message || 
          error.response?.data?.error ||
          error.message || 
          'Failed to submit application. Please try again.';
      alert(`Application submission failed: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1Amount
            data={applicationData}
            onUpdate={updateApplicationData}
            onNext={goToNextStep}
          />
        );
      case 2:
        return (
          <Step2PersonalDetails
            data={applicationData}
            onUpdate={updateApplicationData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
          />
        );
      case 3:
        return (
          <Step3BankAccount
            data={applicationData}
            onUpdate={updateApplicationData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
          />
        );
      case 4:
        return (
          <Step4NSFASVerification
            data={applicationData}
            onUpdate={updateApplicationData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
          />
        );
      case 5:
        return (
          <Step5TermsAndConditions
            data={applicationData}
            onUpdate={updateApplicationData}
            onSubmit={submitApplication}
            onBack={goToPreviousStep}
            onEditStep={setCurrentStep}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="application-loading">
        <div className="loading-spinner"></div>
        <p>Redirecting to login...</p>
      </div>
    );
  }

  if (eligibilityBlock) {
    return (
      <div className="loan-application-flow" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '2rem', textAlign: 'center' }}>
        <div style={{ background: '#ffedd5', borderRadius: 12, padding: '2rem', maxWidth: 520 }}>
          <h2 style={{ color: '#9a3412', marginBottom: '0.75rem' }}>Not Eligible Right Now</h2>
          <p style={{ color: '#78350f', lineHeight: 1.6, marginBottom: '1rem' }}>{eligibilityBlock}</p>
          <p style={{ color: '#92400e', fontSize: '0.9rem', lineHeight: 1.5, marginBottom: '1.5rem' }}>
            You can review each loan’s status and outstanding balance under{' '}
            <strong>Loan History</strong> (Payment History → My Loans).
            Only loans with a balance still owing count as active.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/dashboard/history')}
              style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '0.75rem 1.5rem', fontSize: '1rem', cursor: 'pointer', fontWeight: 600 }}
            >
              View My Loans
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              style={{ background: 'transparent', color: '#9a3412', border: '1.5px solid #fdba74', borderRadius: 8, padding: '0.75rem 1.5rem', fontSize: '1rem', cursor: 'pointer', fontWeight: 600 }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="loan-application-flow">
      {/* Header */}
      <div className="application-header">
        <button 
          className="back-to-home-btn"
          onClick={() => navigate('/')}
          type="button"
        >
          <span className="back-arrow">←</span>
          <span className="back-text">Back to Home</span>
        </button>
        <div className="header-content">
          <div className="header-text">
            <h1 className="application-title">Loan Application</h1>
            <p className="application-subtitle">
              Complete your R{applicationData.loanAmount || 400} loan application in 5 easy steps
            </p>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <StepIndicator
        steps={STEPS}
        currentStep={currentStep}
        completedSteps={currentStep - 1}
      />

      {/* Step Content */}
      <div className={`step-content${currentStep === 5 ? ' step-content--review' : ''}`}>
        {currentStep === 5 ? (
          renderCurrentStep()
        ) : (
          <div className="step-container">{renderCurrentStep()}</div>
        )}
      </div>

      {/* Progress Summary (hidden on review step — step 5 has its own footer) */}
      {currentStep !== 5 && (
      <div className="progress-summary">
        <div className="summary-content">
          <div className="progress-info">
            <div className="step-progress-visual">
              {STEPS.map((step) => (
                <div 
                  key={step.id}
                  className={`step-dot ${step.id < currentStep ? 'completed' : ''} ${step.id === currentStep ? 'current' : ''}`}
                />
              ))}
            </div>
            <div className="step-text-info">
              <span className="step-counter">
                Step {currentStep} of {STEPS.length}
              </span>
              <span className="current-step-title">
                {STEPS[currentStep - 1]?.title}
              </span>
            </div>
          </div>
          <div className="loan-summary" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', padding: '0.75rem 1.5rem', background: 'rgba(0, 116, 217, 0.15)', borderRadius: '12px', border: '1px solid rgba(0, 116, 217, 0.3)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <span style={{ fontSize: '1.75rem', fontWeight: 800, color: '#0074D9', lineHeight: 1 }}>
                R{String(applicationData.loanAmount || 400)}
              </span>
              <span style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '0.25rem' }}>
                Loan Amount
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #2ECC40, #059669)', borderRadius: '20px', color: 'white', fontSize: '0.75rem', fontWeight: 600 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
              Secure
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default LoanApplicationFlow;
